// =============================================================================
// app/src/componentes/EscolherParcelas.tsx — a telinha do crédito (087).
//
// Pedido do dono (28/09/2026): "quando o método de pagamento for crédito,
// abrir uma telinha para escolher em quantas vezes foi, com a lista de quantas
// vezes com o valor parcelado, para termos a informação da taxa de
// parcelamento".
//
// PRIMEIRO A BANDEIRA, DEPOIS AS VEZES, e não os dois numa tela só: a taxa
// depende da bandeira, então uma lista de 12 parcelas antes de saber a
// bandeira mostraria 12 números que podem estar todos errados. São dois toques
// em vez de um, e é o mínimo para o número ser verdade.
//
// O QUE ELA NÃO FAZ: não muda o que o cliente paga. Parcelar não encarece a
// venda — a maquininha desconta da loja. A coluna "loja recebe" existe
// exatamente para isso ficar visível na hora, e não no extrato do mês que vem.
//
// Bandeira sem taxa cadastrada NÃO APARECE. Mostrar a opção com taxa zero
// seria pior que não mostrar: a venda entraria parecendo render 100%.
// =============================================================================
import { useEffect, useState } from 'react';
import type { BandeiraCartao, TaxaDeCartao } from '@core/types';
import {
  bandeirasComCredito, NOME_DA_BANDEIRA, opcoesDeParcelamento, textoDaParcela,
} from '../lib/parcelamento';
import { brl } from '../lib/formato';
import Aviso from './Aviso';

export default function EscolherParcelas({ total, taxas, aoEscolher, aoFechar }: {
  /** O que o cliente vai pagar. Não muda com o parcelamento. */
  total: number;
  taxas: readonly TaxaDeCartao[];
  aoEscolher: (escolha: { bandeira: BandeiraCartao; parcelas: number }) => void;
  aoFechar: () => void;
}) {
  const [bandeira, setBandeira] = useState<BandeiraCartao | null>(null);
  const disponiveis = bandeirasComCredito(taxas);

  // Esc fecha. No balcão o vendedor tem teclado, e abrir uma tela sem saída
  // pelo teclado é o tipo de coisa que faz cancelar a venda inteira.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  const opcoes = bandeira ? opcoesDeParcelamento(taxas, bandeira, total) : [];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4"
         role="dialog" aria-modal="true" aria-label="Em quantas vezes">
      <div className="mx-auto flex min-h-0 w-full max-w-lg flex-col">
        <div className="flex items-start justify-between gap-3 pb-3">
          <div>
            <p className="rotulo">Crédito</p>
            <p className="text-sm text-mute">
              {bandeira ? 'Em quantas vezes?' : 'Qual bandeira?'} ·{' '}
              <span className="font-mono tabular-nums">{brl(total)}</span>
            </p>
          </div>
          <button type="button" className="botao-secundario shrink-0 py-1.5 text-xs"
                  onClick={aoFechar}>
            Voltar
          </button>
        </div>

        {disponiveis.length === 0 && (
          <Aviso tipo="alerta">
            Nenhuma bandeira com taxa cadastrada. Cadastre as taxas antes de
            vender no crédito — sem elas a venda entraria como se a maquininha
            não cobrasse nada.
          </Aviso>
        )}

        {!bandeira && disponiveis.length > 0 && (
          <ul className="min-h-0 space-y-2 overflow-y-auto">
            {disponiveis.map((b) => (
              <li key={b}>
                <button type="button"
                        className="cartao w-full p-3 text-left hover:border-signal"
                        onClick={() => setBandeira(b)}>
                  <span className="text-sm font-bold">{NOME_DA_BANDEIRA[b]}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {bandeira && (
          <>
            <button type="button" className="pb-2 text-left text-xs text-dim underline"
                    onClick={() => setBandeira(null)}>
              ← trocar a bandeira ({NOME_DA_BANDEIRA[bandeira]})
            </button>
            <ul className="min-h-0 space-y-1.5 overflow-y-auto">
              {opcoes.map((o) => (
                <li key={o.parcelas}>
                  <button type="button"
                          className="cartao flex w-full items-center justify-between gap-3 p-3 text-left hover:border-signal"
                          onClick={() => aoEscolher({ bandeira, parcelas: o.parcelas })}>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold tabular-nums">
                        {textoDaParcela(o, brl)}
                      </span>
                      {/* A taxa e o líquido em cinza: são informação da LOJA,
                          não do cliente. O vendedor lê a linha de cima em voz
                          alta e a de baixo para si. */}
                      <span className="block text-xs text-dim tabular-nums">
                        taxa {o.taxaPct.toFixed(2).replace('.', ',')}% ·{' '}
                        {brl(o.taxaEmReais)}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-[0.6875rem] text-dim">loja recebe</span>
                      <span className="block font-mono text-sm tabular-nums">{brl(o.liquido)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
